/**
 * Agents showcase drawer — FG slots + BG runs.
 * First-class UI for mini-Zanas (not buried).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  listFgAgents, subscribeFgAgents, FgAgentSlot, FG_MAX,
} from '../services/zanaAgentRegistry';
import {
  listZanaAgentRuns, startZanaBackgroundAgent, ZanaAgentRun,
} from '../services/briefingsApi';

export function ZanaAgentsDrawer({
  orgCode,
  onWalkthroughReport,
}: {
  orgCode: string;
  /** Optional: walk the owner through a finished BG report (teach). */
  onWalkthroughReport?: (run: ZanaAgentRun) => void;
}) {
  const [open, setOpen] = useState(false);
  const [fg, setFg] = useState<FgAgentSlot[]>(() => listFgAgents());
  const [bg, setBg] = useState<ZanaAgentRun[]>([]);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => subscribeFgAgents(() => setFg(listFgAgents())), []);

  const refreshBg = useCallback(async () => {
    if (!orgCode) return;
    const res = await listZanaAgentRuns(orgCode);
    if (res.ok && res.runs) setBg(res.runs);
  }, [orgCode]);

  useEffect(() => {
    if (!open) return;
    void refreshBg();
    const t = window.setInterval(() => { void refreshBg(); }, 12_000);
    return () => window.clearInterval(t);
  }, [open, refreshBg]);

  const activeFg = fg.filter((s) => s.status !== 'done' && s.status !== 'failed' && s.status !== 'stopped');
  const activeBg = bg.filter((r) => r.status === 'running' || r.status === 'queued');
  const badge = activeFg.length + activeBg.length;

  const startBgScan = async () => {
    setBusy(true);
    setHint(null);
    const res = await startZanaBackgroundAgent(orgCode, {
      topic: 'alerts',
      goal: 'Scan findings in the background',
      background: true,
    });
    setBusy(false);
    if (!res.ok) {
      setHint(res.message || 'Could not start background agent.');
      return;
    }
    setHint(res.spawnLine || `Background · ${res.name || 'Agent'} started.`);
    void refreshBg();
  };

  return (
    <>
      <button
        type="button"
        aria-label="Zana agents"
        onClick={() => setOpen((o) => !o)}
        style={{
          position: 'fixed',
          right: 18,
          bottom: 22,
          zIndex: 5800,
          border: '1px solid rgba(155,124,255,.45)',
          background: 'linear-gradient(140deg,#1a1236,#2a1a55)',
          color: '#efe9ff',
          borderRadius: 14,
          padding: '10px 14px',
          fontWeight: 800,
          fontSize: 12.5,
          cursor: 'pointer',
          boxShadow: '0 12px 28px -14px rgba(40,20,80,.9)',
          fontFamily: 'inherit',
        }}
      >
        Agents{badge ? ` · ${badge}` : ''}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Zana agents"
          style={{
            position: 'fixed',
            right: 18,
            bottom: 68,
            zIndex: 5801,
            width: 'min(360px, 92vw)',
            maxHeight: 'min(70vh, 520px)',
            overflow: 'auto',
            borderRadius: 16,
            background: 'radial-gradient(120% 140% at 100% 0%,#2f2058 0%,#160f2c 60%)',
            border: '1px solid rgba(155,124,255,.35)',
            boxShadow: '0 24px 50px -20px rgba(20,10,50,.95)',
            color: '#efe9ff',
            padding: 14,
            fontFamily: 'inherit',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800 }}>Zana · Agents</div>
              <div style={{ fontSize: 11, color: '#a99fce', marginTop: 2 }}>
                Foreground ≤{FG_MAX} · background off-desk
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                background: 'transparent', border: '1px solid rgba(255,255,255,.2)',
                color: '#c4b3ff', borderRadius: 8, padding: '4px 8px', cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>

          <section style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', color: '#c4b3ff', marginBottom: 6 }}>
              ON THE DESK
            </div>
            {activeFg.length === 0 ? (
              <div style={{ fontSize: 12.5, color: '#8d84b0' }}>No mini-Zanas driving right now.</div>
            ) : activeFg.map((a) => (
              <div key={a.agentId} style={{
                padding: '8px 10px', marginBottom: 6, borderRadius: 10,
                background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.08)',
              }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>Zana · {a.name}</div>
                <div style={{ fontSize: 11.5, color: '#c4b3ff', marginTop: 2 }}>{a.taskTitle}</div>
                <div style={{ fontSize: 10, color: '#8d84b0', marginTop: 2 }}>
                  {a.status} · {a.mode}
                </div>
              </div>
            ))}
          </section>

          <section style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', color: '#c4b3ff', marginBottom: 6 }}>
              BACKGROUND
            </div>
            {bg.length === 0 ? (
              <div style={{ fontSize: 12.5, color: '#8d84b0', marginBottom: 8 }}>No background runs yet.</div>
            ) : bg.slice(0, 8).map((r) => (
              <div key={r.id} style={{
                padding: '8px 10px', marginBottom: 6, borderRadius: 10,
                background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.08)',
              }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>Zana · {r.name}</div>
                <div style={{ fontSize: 11.5, color: '#c4b3ff', marginTop: 2 }}>
                  {r.status}{r.progress != null ? ` · ${r.progress}%` : ''}
                </div>
                {r.report?.summary ? (
                  <div style={{ fontSize: 11.5, color: '#ddd6f5', marginTop: 4 }}>{r.report.summary}</div>
                ) : null}
                {r.status === 'done' && onWalkthroughReport ? (
                  <button
                    type="button"
                    onClick={() => onWalkthroughReport(r)}
                    style={{
                      marginTop: 6, border: '1px solid rgba(155,124,255,.4)', background: 'rgba(155,124,255,.18)',
                      color: '#efe9ff', borderRadius: 8, padding: '5px 10px', fontSize: 11.5,
                      fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    Walk me through what you did
                  </button>
                ) : null}
              </div>
            ))}
            <button
              type="button"
              disabled={busy}
              onClick={() => void startBgScan()}
              style={{
                width: '100%', marginTop: 4, border: 'none',
                background: 'linear-gradient(120deg,#4b3aa8,#6f4ff0)',
                color: '#fff', borderRadius: 10, padding: '9px 12px',
                fontWeight: 800, fontSize: 12.5, cursor: busy ? 'wait' : 'pointer',
                opacity: busy ? 0.6 : 1, fontFamily: 'inherit',
              }}
            >
              Start background scan
            </button>
            {hint ? (
              <div style={{ fontSize: 11.5, color: '#c4b3ff', marginTop: 8 }}>{hint}</div>
            ) : null}
          </section>
        </div>
      )}
    </>
  );
}
